import { useCallback, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { RiBookmarkFill, RiBookmarkLine } from 'react-icons/ri'
import type { IQuestionBankEntryDetail, IQuestionBankEntrySummary } from '@brushfire/types'
import {
  useCreateQuestionBankEntry,
  useUpdateQuestionBankEntry,
} from '@/features/question-bank/api/question-bank-mutations'
import { fetchQuestionBankEntryById } from '@/features/question-bank/api/question-bank-queries'
import {
  bankEntryToBuilderQuestion,
  builderQuestionBankContentFingerprint,
  builderQuestionToBankPayload,
} from '@/features/question-bank/lib/questionBankMappers'
import { isDuplicateQuestionBankPrompt } from '@/features/question-bank/lib/normalizePrompt'
import { validateBuilderQuestionForSave } from '@/features/instruments/domain/builderQuestionValidation'
import type { BuilderQuestion } from '@/features/instruments/question-builder/types'
import { questionBankSaveBookmarkButtonSx } from '@/shared/lib/libraryCardHover'

type PopoverMode = 'duplicate' | 'update' | null

/** Shown when bookmark save is blocked — inline field errors carry specifics. */
export const QUESTION_BANK_BOOKMARK_VALIDATION_TOOLTIP =
  'Fix all validation errors before saving to the Question Bank.'

type Props = Readonly<{
  question: BuilderQuestion
  isFilled: boolean
  bankEntries: readonly IQuestionBankEntrySummary[]
  onSaved: (entry: IQuestionBankEntryDetail) => void
  /** Enables inline save-validation errors for this question (all fields). */
  onQuestionTextValidationRequested?: () => void
  /** Profile questions keep the bookmark on. */
  locked?: boolean
}>

export function QuestionBankBookmarkButton({
  question,
  isFilled,
  bankEntries,
  onSaved,
  onQuestionTextValidationRequested,
  locked = false,
}: Props) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [validationTooltipOpen, setValidationTooltipOpen] = useState(false)
  const [popoverMode, setPopoverMode] = useState<PopoverMode>(null)
  const [isCheckingDirty, setIsCheckingDirty] = useState(false)
  const createMutation = useCreateQuestionBankEntry()
  const updateMutation = useUpdateQuestionBankEntry()
  const isPending = createMutation.isPending || updateMutation.isPending || isCheckingDirty

  const linkedBankEntryId = question.questionBankEntryId?.trim() || null
  const existsInBankByContent = isDuplicateQuestionBankPrompt(question.questionText, bankEntries)
  /** Filled when linked, saved this session, or prompt already exists in the bank. */
  const displayFilled = isFilled || Boolean(linkedBankEntryId) || existsInBankByContent

  const closePopover = useCallback(() => setPopoverMode(null), [])

  const saveToBank = useCallback(
    async (forceDuplicate: boolean) => {
      const payload = builderQuestionToBankPayload(question)
      try {
        const entry = await createMutation.mutateAsync({ ...payload, forceDuplicate })
        onSaved(entry)
        closePopover()
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 409 && !forceDuplicate) {
          setPopoverMode('duplicate')
          return
        }
        closePopover()
      }
    },
    [closePopover, createMutation, onSaved, question],
  )

  const updateBankEntry = useCallback(async () => {
    if (!linkedBankEntryId) return
    const payload = builderQuestionToBankPayload(question)
    try {
      const entry = await updateMutation.mutateAsync({ id: linkedBankEntryId, input: payload })
      onSaved(entry)
      closePopover()
    } catch {
      // keep popover open; error text below
    }
  }, [closePopover, linkedBankEntryId, onSaved, question, updateMutation])

  const handleFilledBankClick = useCallback(async () => {
    if (!linkedBankEntryId) return
    setIsCheckingDirty(true)
    try {
      const entry = await fetchQuestionBankEntryById(linkedBankEntryId)
      const fromBank = bankEntryToBuilderQuestion(entry, 0)
      const current = builderQuestionBankContentFingerprint(question)
      const saved = builderQuestionBankContentFingerprint(fromBank)
      if (current === saved) return
      setPopoverMode('update')
    } catch {
      // If the bank entry is gone, offer update/add so the user can still save.
      setPopoverMode('update')
    } finally {
      setIsCheckingDirty(false)
    }
  }, [linkedBankEntryId, question])

  const handleClick = () => {
    if (isPending || locked) return
    const saveErr = validateBuilderQuestionForSave(question, 'draft')
    if (saveErr) {
      setValidationTooltipOpen(true)
      onQuestionTextValidationRequested?.()
      return
    }
    setValidationTooltipOpen(false)

    if (linkedBankEntryId) {
      void handleFilledBankClick()
      return
    }

    // Prompt already in bank (no local link) — same Add Anyway path as before.
    if (existsInBankByContent) {
      setPopoverMode('duplicate')
      return
    }

    if (displayFilled) return

    void saveToBank(false)
  }

  const ariaLabel = (() => {
    if (!displayFilled) return 'Save to Question Bank'
    if (linkedBankEntryId) return 'Update Question Bank entry'
    return 'Saved to Question Bank'
  })()

  return (
    <ClickAwayListener onClickAway={() => setValidationTooltipOpen(false)}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Tooltip
          open={validationTooltipOpen}
          title={QUESTION_BANK_BOOKMARK_VALIDATION_TOOLTIP}
          placement="bottom-start"
          arrow
          disableHoverListener
          disableFocusListener
          disableTouchListener
          data-testid="question-bank-bookmark-validation-tooltip"
        >
          <IconButton
            ref={anchorRef}
            aria-label={ariaLabel}
            data-testid="question-bank-bookmark"
            aria-pressed={displayFilled}
            onClick={handleClick}
            disabled={isPending}
            sx={{
              ...questionBankSaveBookmarkButtonSx,
              color: displayFilled ? '#008cd0' : '#64748b',
            }}
          >
            {displayFilled ? (
              <RiBookmarkFill size={18} aria-hidden data-testid="question-bank-bookmark-filled" />
            ) : (
              <RiBookmarkLine size={18} aria-hidden data-testid="question-bank-bookmark-outline" />
            )}
          </IconButton>
        </Tooltip>

        <Popover
          open={popoverMode === 'duplicate'}
          anchorEl={anchorRef.current}
          onClose={closePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, maxWidth: 280, borderRadius: '10px' } } }}
          data-testid="question-bank-bookmark-duplicate-popover"
        >
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#0f172a', mb: 1.5 }}>
              This question already exists in the Question Bank. Add anyway?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={closePopover}
                data-testid="question-bank-duplicate-cancel"
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => void saveToBank(true)}
                disabled={isPending}
                data-testid="question-bank-duplicate-add"
                sx={{ textTransform: 'none' }}
              >
                Add Anyway
              </Button>
            </Box>
            {createMutation.isError ? (
              <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 1 }}>
                Failed to save question. Please try again.
              </Typography>
            ) : null}
          </Box>
        </Popover>

        <Popover
          open={popoverMode === 'update'}
          anchorEl={anchorRef.current}
          onClose={closePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, maxWidth: 300, borderRadius: '10px' } } }}
          data-testid="question-bank-bookmark-update-popover"
        >
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#0f172a', mb: 1.5 }}>
              This Question Bank question was edited. Update the original, or add a new entry?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => void updateBankEntry()}
                disabled={isPending}
                data-testid="question-bank-update"
                sx={{ textTransform: 'none' }}
              >
                Update
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void saveToBank(true)}
                disabled={isPending}
                data-testid="question-bank-update-add-anyway"
                sx={{ textTransform: 'none' }}
              >
                Add Anyway
              </Button>
            </Box>
            {updateMutation.isError || createMutation.isError ? (
              <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 1 }}>
                Failed to save question. Please try again.
              </Typography>
            ) : null}
          </Box>
        </Popover>
      </Box>
    </ClickAwayListener>
  )
}
